import { Link } from "react-router-dom";
import { Shield, Mail, Phone } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { BUSINESS } from "../config/business";

// ====================================================================
// PRIVACY POLICY — /privacy
// --------------------------------------------------------------------
// Plain-English policy for the Woodstone Construction website.
// Update business details via /app/frontend/src/config/business.js.
// ====================================================================

const LAST_UPDATED = "December 2025";

export default function PrivacyPage() {
  useSEO({
    title: "Privacy Policy — Woodstone Construction",
    description:
      "How Woodstone Construction collects and uses your information when you request a quote, use our decking cost calculator, or contact us via the website.",
    canonical:
      typeof window !== "undefined"
        ? `${window.location.origin}/privacy`
        : undefined,
  });

  return (
    <main data-testid="privacy-page">
      {/* ============== HERO ============== */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-6">
        <div className="inline-flex items-center gap-2 bg-[#F3E8E5] text-[#7A2D2F] rounded-full px-3 py-1 text-[12px] uppercase tracking-[0.2em] font-semibold mb-5">
          <Shield size={13} />
          Privacy
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] tracking-tight leading-[1.05] font-medium"
          style={{ fontFamily: "'Fraunces', serif" }}
        >
          Privacy Policy
        </h1>
        <p className="mt-4 text-[#4A4D4A]">Last updated: {LAST_UPDATED}</p>
      </section>

      {/* ============== BODY ============== */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <article className="bg-white border border-[#DCD7CE] rounded-3xl p-7 md:p-12 shadow-[0_8px_30px_rgba(20,40,31,0.04)] space-y-10">
          <div className="text-[#4A4D4A] leading-relaxed">
            <p>
              Woodstone Construction (“we”, “us”, “our”) is a Melbourne-based
              decking, pergola and landscaping business. We take your privacy
              seriously and only ever use the information you share with us to
              do our job — to get back to you with a quote and help you with
              your outdoor project.
            </p>
            <p className="mt-3">
              This page explains, in plain English, what we collect, why, and
              what we do (and don’t do) with it.
            </p>
          </div>

          <Section title="What we collect">
            <p>
              When you use our website, we may collect the following information
              that you choose to give us:
            </p>
            <ul className="list-disc pl-5 mt-3 space-y-1.5">
              <li>
                <strong>Contact details</strong> — your name, phone number,
                email address and suburb.
              </li>
              <li>
                <strong>Project details</strong> — the service you’re
                interested in (decking, pergola, turf, landscaping, etc.) and
                anything you write in the message box.
              </li>
              <li>
                <strong>Optional photos</strong> — if you choose to upload a
                photo of your space with your quote request.
              </li>
              <li>
                <strong>Calculator inputs</strong> — the deck material, size
                and options you enter into our cost calculator, along with the
                estimate range it produces.
              </li>
            </ul>
            <p className="mt-3">
              We also collect basic, anonymous usage data (page views,
              browser/device type, country) through Google Analytics 4 so we
              can see how the website is being used and improve it over time.
            </p>
          </Section>

          <Section title="Why we collect it">
            <p>We use your information for a single purpose: to help you.</p>
            <ul className="list-disc pl-5 mt-3 space-y-1.5">
              <li>To reach out about your enquiry or quote request.</li>
              <li>
                To send you accurate pricing, timelines and follow-up info on
                your project.
              </li>
              <li>
                To answer any questions you have about decking, pergolas, turf
                or landscaping.
              </li>
              <li>
                To improve our website and services using anonymous analytics.
              </li>
            </ul>
          </Section>

          <Section title="Who we share it with">
            <p className="text-[#1A1A1A] font-semibold">
              We do not sell, rent, or share your personal information with any
              third party for marketing.
            </p>
            <p className="mt-3">
              Your contact details stay with Woodstone Construction and are
              only seen by our team for the purpose of getting back to you.
            </p>
            <p className="mt-3">
              The only exceptions are the basic infrastructure services that
              run the website (our website host and our analytics provider,
              Google Analytics) — and they only see anonymous, aggregated
              traffic data.
            </p>
          </Section>

          <Section title="How long we keep it">
            <p>
              We hold onto your enquiry information for as long as we need to
              follow up about your project and for a reasonable period
              afterwards in case you come back to us. If you’d like your
              information removed sooner, just ask — see “Your choices” below.
            </p>
          </Section>

          <Section title="How it’s stored">
            <p>
              Your information is stored in a secure database managed for us
              by our website hosting provider. Access to incoming quote
              requests is protected by a private admin password and is only
              available to authorised people at Woodstone Construction.
            </p>
          </Section>

          <Section title="Cookies & analytics">
            <p>
              We use <strong>Google Analytics 4</strong> to understand how
              visitors find and use our website. Google may use cookies to
              count visits and remember your device, but this information is
              anonymous and we can’t use it to identify you personally.
            </p>
            <p className="mt-3">
              We don’t use cookies for advertising or to track you across other
              websites. You can disable cookies in your browser at any time —
              the site will still work normally.
            </p>
          </Section>

          <Section title="Your choices">
            <p>You always have the right to:</p>
            <ul className="list-disc pl-5 mt-3 space-y-1.5">
              <li>Ask us what information we hold about you.</li>
              <li>Ask us to correct or update your details.</li>
              <li>
                Ask us to delete your information from our database.
              </li>
              <li>
                Tell us to stop contacting you — and we will, immediately.
              </li>
            </ul>
            <p className="mt-3">
              Just send us a quick message using the details below.
            </p>
          </Section>

          <Section title="Get in touch">
            <p>
              Questions about your privacy or this policy? Or want your
              information removed? Reach out anytime:
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <a
                href={`mailto:${BUSINESS.email}`}
                className="flex items-center gap-3 bg-[#F8F7F4] border border-[#DCD7CE] rounded-xl px-4 py-3 hover:border-[#7A2D2F] transition-colors"
                data-testid="privacy-email-link"
              >
                <span className="h-9 w-9 rounded-lg bg-white border border-[#DCD7CE] flex items-center justify-center">
                  <Mail size={15} className="text-[#7A2D2F]" />
                </span>
                <span className="text-sm text-[#1A1A1A] break-all">
                  {BUSINESS.email}
                </span>
              </a>
              <a
                href={`tel:${BUSINESS.phone}`}
                className="flex items-center gap-3 bg-[#F8F7F4] border border-[#DCD7CE] rounded-xl px-4 py-3 hover:border-[#7A2D2F] transition-colors"
                data-testid="privacy-phone-link"
              >
                <span className="h-9 w-9 rounded-lg bg-white border border-[#DCD7CE] flex items-center justify-center">
                  <Phone size={15} className="text-[#7A2D2F]" />
                </span>
                <span className="text-sm text-[#1A1A1A]">
                  {BUSINESS.phoneDisplay}
                </span>
              </a>
            </div>
          </Section>

          <Section title="Updates to this policy">
            <p>
              If we ever change how we handle your information, we’ll update
              this page and refresh the “Last updated” date at the top. The
              current version was last updated on{" "}
              <strong>{LAST_UPDATED}</strong>.
            </p>
          </Section>

          <div className="pt-4 border-t border-[#EFECE6] flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
            <span className="text-sm text-[#4A4D4A]">
              Got a project to plan?
            </span>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#7A2D2F] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#0D0D0E] transition-all"
              data-testid="privacy-cta-contact"
            >
              Request a quote
            </Link>
          </div>
        </article>
      </section>
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2
        className="text-2xl text-[#1A1A1A] tracking-tight mb-3"
        style={{ fontFamily: "'Fraunces', serif" }}
      >
        {title}
      </h2>
      <div className="text-[#4A4D4A] leading-relaxed">{children}</div>
    </section>
  );
}
